import React, { useState, useEffect, useRef } from "react";
import { Link, useHistory } from "react-router-dom";
import axios from "axios";
import {
  UserIcon,
  MailIcon,
  PhoneIcon,
  LockIcon,
  EyeIcon,
  EyeOffIcon,
  CheckIcon,
  CheckBadgeIcon,
  FileTextIcon,
  ShoppingBagIcon,
  HeadphonesIcon,
  ShieldCheckIcon,
  ArrowRightIcon,
  AlertCircleIcon,
  UsersGroupIcon,
  ClockTimerIcon,
  UserPlusIcon,
  StoreIcon,
  RefreshCwIcon,
} from "../components/Icons";
import AuthHeader from "../components/AuthHeader";
import AuthFooter from "../components/AuthFooter";
import SEO from "../components/SEO";
import registerCoupleImg from "../assets/register-couple-transparent.png";
import { API_URL } from "../services/api";

function splitName(fullName) {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  const firstName = parts.shift() || "";
  const lastName = parts.length > 0 ? parts.join(" ") : "Seller";
  return { firstName, lastName };
}

function getApiErrorMessage(err, fallback = "Something went wrong. Please try again.") {
  if (err.response?.data?.msg) return err.response.data.msg;
  if (err.response?.data?.message) return err.response.data.message;
  if (err.response?.status === 500) return "Server is temporarily unavailable. Please try again in a moment.";
  if (!err.response && err.message?.includes("Network Error")) {
    return "Unable to connect to backend server. Please verify the server is running on port 5000.";
  }
  return err.message || fallback;
}

function Register({ onRegisterSuccess }) {
  const history = useHistory();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [number, setNumber] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [step, setStep] = useState(1); // 1: Form, 2: OTP, 3: Success
  const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState("");
  const [resendSuccess, setResendSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendTimer, setResendTimer] = useState(60);

  const inputRefs = useRef([]);

  // Resend countdown timer
  useEffect(() => {
    let interval = null;
    if (step === 2 && resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [step, resendTimer]);

  // Focus first OTP box on entering Step 2
  useEffect(() => {
    if (step === 2 && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [step]);

  const validateForm = () => {
    if (!fullName.trim()) {
      setError("Please enter your full name.");
      return false;
    }
    if (!email.trim() || !/\S+@\S+\.\S+/.test(email.trim())) {
      setError("Please enter a valid email address.");
      return false;
    }
    if (!number.trim() || !/^\d{10}$/.test(number.trim())) {
      setError("Please enter a valid 10-digit mobile number.");
      return false;
    }
    if (!password || password.length < 6) {
      setError("Password should be at least 6 characters long.");
      return false;
    }
    return true;
  };

  // STEP 1: Submit Details & Request OTP
  const handleInitiateSignup = async (e) => {
    e.preventDefault();
    setError("");
    setResendSuccess("");

    if (!validateForm()) return;

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/verfiy-number`, {
        email: email.trim().toLowerCase(),
        number: number.trim(),
      });

      setOtpDigits(["", "", "", "", "", ""]);
      setResendTimer(60);
      setStep(2);
    } catch (err) {
      const serverMsg = getApiErrorMessage(err, "Unable to send verification code. Please check your details.");
      setError(serverMsg);
    } finally {
      setLoading(false);
    }
  };

  // OTP Input Handlers
  const handleOtpChange = (index, value) => {
    // Only accept numeric digit
    const digit = value.replace(/\D/g, "").slice(-1);
    const newDigits = [...otpDigits];
    newDigits[index] = digit;
    setOtpDigits(newDigits);
    setError("");

    // Auto advance focus
    if (digit && index < 5 && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (!otpDigits[index] && index > 0 && inputRefs.current[index - 1]) {
        inputRefs.current[index - 1].focus();
      }
    } else if (e.key === "ArrowLeft" && index > 0 && inputRefs.current[index - 1]) {
      inputRefs.current[index - 1].focus();
    } else if (e.key === "ArrowRight" && index < 5 && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasteData) return;

    const newDigits = [...otpDigits];
    for (let i = 0; i < 6; i++) {
      newDigits[i] = pasteData[i] || "";
    }
    setOtpDigits(newDigits);
    setError("");

    const targetIndex = Math.min(pasteData.length, 5);
    if (inputRefs.current[targetIndex]) {
      inputRefs.current[targetIndex].focus();
    }
  };

  // STEP 2: Verify OTP & Create Account
  const handleVerifyOtpAndCreate = async (e) => {
    e.preventDefault();
    setError("");
    setResendSuccess("");

    const completeOtp = otpDigits.join("");
    if (completeOtp.length < 6) {
      setError("Please enter the complete 6-digit verification code.");
      return;
    }

    setLoading(true);
    try {
      const { firstName, lastName } = splitName(fullName);

      // Verify OTP code
      try {
        await axios.post(`${API_URL}/api/verfiy-otp`, {
          email: email.trim().toLowerCase(),
          number: number.trim(),
          otp: completeOtp,
        });
      } catch (otpErr) {
        const otpErrorMsg = getApiErrorMessage(otpErr, "Invalid verification code. Please check and try again.");
        setError(otpErrorMsg);
        setLoading(false);
        return;
      }

      // Create vendor account
      const signupRes = await axios.post(`${API_URL}/api/Signin`, {
        fullName: fullName.trim(),
        firstName,
        lastName,
        email: email.trim().toLowerCase(),
        number: number.trim(),
        password,
        confirmPassword: password,
        gender: "not specified",
      });

      const user = signupRes.data?.user || {
        firstName,
        lastName,
        email: email.trim().toLowerCase(),
        vendorStatus: "none",
      };
      const token = signupRes.data?.token || "mock_vendor_token";

      if (token) {
        localStorage.setItem("vendor_token", token);
        localStorage.setItem("vendor_user", JSON.stringify(user));
        if (onRegisterSuccess) {
          onRegisterSuccess(user, token);
        }
      }

      setStep(3);
    } catch (err) {
      const serverMsg = getApiErrorMessage(err, "Unable to create account. Please try again.");
      setError(serverMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendTimer > 0 || isResending) return;
    setError("");
    setResendSuccess("");
    setIsResending(true);

    try {
      const res = await axios.post(`${API_URL}/api/verfiy-number`, {
        email: email.trim().toLowerCase(),
        number: number.trim(),
      });

      const successMsg = res.data?.message || res.data?.msg || `A new verification code has been sent to ${email}`;
      setResendSuccess(successMsg);
      setOtpDigits(["", "", "", "", "", ""]);
      setResendTimer(60);
      if (inputRefs.current[0]) inputRefs.current[0].focus();
    } catch (err) {
      setError(getApiErrorMessage(err, "Unable to resend verification code. Please try again."));
    } finally {
      setIsResending(false);
    }
  };

  const handleContinueToSetup = () => {
    history.push("/vendor/apply");
  };

  return (
    <div className="reg-page-wrapper">
      <SEO
        title="Seller Registration | Sell Shoes & Fashion Online | IM Online Seller Portal"
        description="Sign up as an IM Online seller in 2 minutes. Start selling footwear, sneakers, and apparel across India with 0% commission plans and automated bank settlements."
        keywords="seller registration online, register as footwear seller, sell shoes im online, become online merchant india, seller signup footwear"
        canonical="https://vendor.imonline.shop/vendor/register"
        ogTitle="Register as an IM Online Footwear Seller"
        ogDescription="Sign up free and join hundreds of shoe and fashion merchants on IM Online Seller Center."
      />
      <AuthHeader current="register" />

      <main className="reg-main-content">
        <div className="reg-container">

          {step === 3 ? (
            /* ══════════════════ STEP 3: SUCCESS STATE ══════════════════ */
            <div className="reg-success-card-wrap">
              <div className="reg-success-card">
                <div className="reg-success-icon-wrap">
                  <CheckIcon size={40} color="#16a34a" />
                </div>

                <span className="reg-success-badge">ACCOUNT READY</span>

                <h2 className="reg-success-title">
                  Account Created Successfully! 🎉
                </h2>

                <p className="reg-success-desc">
                  Welcome to IM Online, <strong>{fullName}</strong>! Your seller account is ready. Complete your basic business verification to begin listing products.
                </p>

                <div className="reg-success-progress-box">
                  <div className="reg-success-progress-label">YOUR SELLER PROGRESS</div>

                  <div className="reg-success-step-row done">
                    <div className="reg-step-dot done">
                      <CheckIcon size={14} color="#16a34a" />
                    </div>
                    <span className="reg-step-text done">
                      Account created ({email})
                    </span>
                  </div>

                  <div className="reg-success-step-row active">
                    <div className="reg-step-dot active">2</div>
                    <div className="reg-step-text-wrap">
                      <span className="reg-step-text active">
                        Next: Complete seller verification
                      </span>
                      <p className="reg-step-sub">
                        Provide business details &amp; fulfillment address (2 mins)
                      </p>
                    </div>
                  </div>
                </div>

                <div className="reg-success-actions">
                  <button
                    onClick={handleContinueToSetup}
                    className="reg-submit-btn w-100"
                  >
                    Continue Seller Setup <ArrowRightIcon size={18} />
                  </button>
                  <Link
                    to="/vendor/login"
                    className="reg-btn-ghost-outline w-100"
                    style={{ justifyContent: "center", textAlign: "center" }}
                  >
                    Go to Sign In
                  </Link>
                </div>
              </div>
            </div>
          ) : (
            /* ═════════════════ STEP 1 & 2: REGISTRATION ═════════════════ */
            <>
              {/* Top Hero Banner Section */}
              <div className="reg-top-hero-row">
                <div className="reg-top-hero-left">
                  <span className="reg-pill-badge">
                    SELLER REGISTRATION
                  </span>
                  <h1 className="reg-hero-title">
                    Create Your Seller Account
                  </h1>
                  <p className="reg-hero-sub">
                    Start your journey with IM Online and bring your products to thousands of customers.
                  </p>

                  {/* Two-Step Progress Bar */}
                  <div className="reg-steps-tracker">
                    <div className={`reg-tracker-step ${step === 1 ? "active" : "completed"}`}>
                      <div className="reg-tracker-step-header">
                        <div className="reg-tracker-num-badge">
                          {step > 1 ? <CheckIcon size={16} color="#16a34a" /> : "1"}
                        </div>
                        <div className="reg-tracker-info">
                          <div className="reg-tracker-title">Account Information</div>
                          <div className="reg-tracker-desc">
                            {step > 1 ? "Completed" : "Basic details to get started"}
                          </div>
                        </div>
                      </div>
                      {step === 1 && <div className="reg-tracker-line-indicator" />}
                    </div>

                    <div className={`reg-tracker-step ${step === 2 ? "active" : "pending"}`}>
                      <div className="reg-tracker-step-header">
                        <div className="reg-tracker-num-badge">2</div>
                        <div className="reg-tracker-info">
                          <div className="reg-tracker-title">Email Verification</div>
                          <div className="reg-tracker-desc">Confirm your email address</div>
                        </div>
                      </div>
                      {step === 2 && <div className="reg-tracker-line-indicator" />}
                    </div>
                  </div>
                </div>

                {/* Right Hero Illustration */}
                <div className="reg-top-hero-right">
                  <div className="reg-couple-visual-stage">
                    <div className="reg-couple-blob-bg" />
                    <div className="reg-couple-dots-decor" />
                    <img
                      src={registerCoupleImg}
                      alt="Happy Indian Sellers"
                      className="reg-couple-img"
                    />
                    <div className="reg-couple-floating-icon">
                      <StoreIcon size={20} color="#a85050" />
                    </div>
                  </div>
                </div>
              </div>

              {/* 2-Column Cards Grid */}
              <div className="reg-grid-layout">
                {/* ── LEFT COLUMN: FORM CARD ── */}
                <div className="reg-form-card">
                  <div className="reg-card-header">
                    <h2 className="reg-card-title">
                      {step === 1 ? "Account Information" : "Verify Email Address"}
                    </h2>
                    <span className="reg-step-pill">
                      Step {step} of 2
                    </span>
                  </div>

                  {error && (
                    <div className="reg-error-alert">
                      <AlertCircleIcon size={20} color="#dc2626" className="flex-shrink-0 mt-0.5" />
                      <span>{error}</span>
                    </div>
                  )}

                  {resendSuccess && (
                    <div className="reg-success-alert">
                      <CheckIcon size={20} color="#16a34a" className="flex-shrink-0 mt-0.5" />
                      <span>{resendSuccess}</span>
                    </div>
                  )}

                  {step === 1 ? (
                    <form onSubmit={handleInitiateSignup} className="reg-form-body">
                      {/* Full Name */}
                      <div className="reg-form-group">
                        <label className="reg-form-label" htmlFor="reg-fullname">
                          Full Name <span className="reg-required-star">*</span>
                        </label>
                        <div className="reg-input-group">
                          <span className="reg-input-icon">
                            <UserIcon size={20} />
                          </span>
                          <input
                            id="reg-fullname"
                            type="text"
                            className="reg-input-field"
                            required
                            value={fullName}
                            onChange={(e) => setFullName(e.target.value)}
                            placeholder="Enter your full name"
                            autoComplete="name"
                          />
                        </div>
                      </div>

                      {/* Email Address */}
                      <div className="reg-form-group">
                        <label className="reg-form-label" htmlFor="reg-email">
                          Email Address <span className="reg-required-star">*</span>
                        </label>
                        <div className="reg-input-group">
                          <span className="reg-input-icon">
                            <MailIcon size={20} />
                          </span>
                          <input
                            id="reg-email"
                            type="email"
                            className="reg-input-field"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Enter your email address"
                            autoComplete="email"
                          />
                        </div>
                        <p className="reg-input-hint">We will send a verification code to this email.</p>
                      </div>

                      {/* Mobile Number */}
                      <div className="reg-form-group">
                        <label className="reg-form-label" htmlFor="reg-number">
                          Mobile Number <span className="reg-required-star">*</span>
                        </label>
                        <div className="reg-phone-input-combo">
                          <div className="reg-phone-prefix">
                            <span className="reg-prefix-icon"><PhoneIcon size={16} /></span>
                            <span className="reg-prefix-text">+91</span>
                            <span className="reg-prefix-chevron">▾</span>
                          </div>
                          <div className="reg-input-group reg-phone-right">
                            <input
                              id="reg-number"
                              type="tel"
                              className="reg-input-field no-left-icon"
                              required
                              maxLength="10"
                              value={number}
                              onChange={(e) => setNumber(e.target.value.replace(/\D/g, ""))}
                              placeholder="Enter your mobile number"
                              autoComplete="tel"
                            />
                          </div>
                        </div>
                        <p className="reg-input-hint">We will send a verification code to this number.</p>
                      </div>

                      {/* Password */}
                      <div className="reg-form-group">
                        <label className="reg-form-label" htmlFor="reg-password">
                          Password <span className="reg-required-star">*</span>
                        </label>
                        <div className="reg-input-group">
                          <span className="reg-input-icon">
                            <LockIcon size={20} />
                          </span>
                          <input
                            id="reg-password"
                            type={showPassword ? "text" : "password"}
                            className="reg-input-field has-toggle-btn"
                            required
                            minLength="6"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Create a strong password"
                            autoComplete="new-password"
                          />
                          <button
                            type="button"
                            className="reg-password-toggle-btn"
                            onClick={() => setShowPassword(!showPassword)}
                            aria-label={showPassword ? "Hide password" : "Show password"}
                          >
                            {showPassword ? <EyeOffIcon size={20} /> : <EyeIcon size={20} />}
                          </button>
                        </div>
                        <p className="reg-input-hint">Use 8+ characters with letters, numbers and symbols.</p>
                      </div>

                      {/* Submit Button */}
                      <button
                        type="submit"
                        className="reg-submit-btn"
                        disabled={loading}
                      >
                        {loading ? (
                          <span>
                            <span className="spinner-border spinner-border-sm me-2" role="status" />
                            Sending Verification Code...
                          </span>
                        ) : (
                          <span>
                            Continue <ArrowRightIcon size={18} />
                          </span>
                        )}
                      </button>

                      {/* Security Footer Note */}
                      <div className="reg-form-security-note">
                        <LockIcon size={15} color="#787570" />
                        <span>Your information is secure with us</span>
                      </div>
                    </form>
                  ) : (
                    /* ── STEP 2: OTP VERIFICATION ── */
                    <form onSubmit={handleVerifyOtpAndCreate} className="reg-otp-container">
                      <div className="reg-otp-header">
                        <div className="reg-otp-icon-circle">
                          <MailIcon size={30} color="#a85050" />
                        </div>
                        <h3 className="reg-otp-heading">Check Your Email</h3>
                        <p className="reg-otp-subtext">
                          We sent a 6-digit verification code to
                        </p>
                        <div className="reg-otp-email-chip">
                          <MailIcon size={16} color="#a85050" />
                          <span className="reg-otp-email-val">{email}</span>
                          <button
                            type="button"
                            onClick={() => {
                              setStep(1);
                              setError("");
                              setResendSuccess("");
                            }}
                            className="reg-otp-edit-chip-btn"
                            title="Edit email address"
                          >
                            Edit
                          </button>
                        </div>
                      </div>

                      <div className="reg-otp-boxes-wrap">
                        <label className="reg-otp-boxes-label">
                          Enter 6-Digit Code
                        </label>
                        <div className="reg-otp-boxes-grid" onPaste={handleOtpPaste}>
                          {otpDigits.map((digit, idx) => (
                            <input
                              key={idx}
                              ref={(el) => (inputRefs.current[idx] = el)}
                              type="text"
                              inputMode="numeric"
                              maxLength="1"
                              value={digit}
                              onChange={(e) => handleOtpChange(idx, e.target.value)}
                              onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                              className={`reg-otp-digit-box ${digit ? "filled" : ""}`}
                              autoComplete="off"
                            />
                          ))}
                        </div>
                      </div>

                      {/* Resend OTP Bar */}
                      <div className="reg-otp-resend-bar">
                        {resendTimer > 0 ? (
                          <div className="reg-otp-timer-box">
                            <ClockTimerIcon size={16} color="#787570" />
                            <span>
                              Resend code in <strong>00:{resendTimer < 10 ? `0${resendTimer}` : resendTimer}</strong>
                            </span>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={handleResendOtp}
                            disabled={isResending}
                            className="reg-otp-resend-action-btn"
                          >
                            {isResending ? (
                              <span>
                                <span className="spinner-border spinner-border-sm me-2" role="status" />
                                Sending code...
                              </span>
                            ) : (
                              <span>
                                <RefreshCwIcon size={15} className="me-1" />
                                Didn't receive the code? <strong>Resend OTP</strong>
                              </span>
                            )}
                          </button>
                        )}
                      </div>

                      {/* Primary Verify Button */}
                      <button
                        type="submit"
                        className="reg-submit-btn"
                        disabled={loading || otpDigits.join("").length < 6}
                      >
                        {loading ? (
                          <span>
                            <span className="spinner-border spinner-border-sm me-2" role="status" />
                            Verifying &amp; Creating Account...
                          </span>
                        ) : (
                          <span>
                            Verify &amp; Create Account <ArrowRightIcon size={18} />
                          </span>
                        )}
                      </button>

                      {/* Secondary Actions */}
                      <div className="reg-otp-bottom-actions">
                        <button
                          type="button"
                          onClick={() => {
                            setStep(1);
                            setError("");
                            setResendSuccess("");
                          }}
                          className="reg-otp-back-details-btn"
                        >
                          ← Back to Edit Details
                        </button>
                      </div>

                      <div className="reg-form-security-note">
                        <LockIcon size={14} color="#787570" />
                        <span>256-bit encrypted &amp; secure verification</span>
                      </div>
                    </form>
                  )}
                </div>

                {/* ── RIGHT COLUMN: "Why sell with IM Online?" ── */}
                <div className="reg-info-panel-card">
                  {/* Top Community Banner */}
                  <div className="reg-community-top-box">
                    <div className="reg-community-icon-box">
                      <UsersGroupIcon size={20} color="#a85050" />
                    </div>
                    <div className="reg-community-text">
                      Join <strong style={{ color: "#a85050" }}>5,000+</strong> Indian sellers growing with IM Online!
                    </div>
                  </div>

                  <h3 className="reg-info-title">
                    Why sell with IM Online?
                  </h3>

                  {/* 5 Feature Rows */}
                  <div className="reg-features-list">
                    {/* Feature 1 */}
                    <div className="reg-feature-row">
                      <div className="reg-feature-icon-square">
                        <UserPlusIcon size={20} color="#a85050" />
                      </div>
                      <div className="reg-feature-text-block">
                        <strong className="reg-feature-name">Simple onboarding</strong>
                        <p className="reg-feature-detail">
                          Start without complicated setup or upfront charges.
                        </p>
                      </div>
                    </div>

                    {/* Feature 2 */}
                    <div className="reg-feature-row">
                      <div className="reg-feature-icon-square">
                        <FileTextIcon size={20} color="#a85050" />
                      </div>
                      <div className="reg-feature-text-block">
                        <strong className="reg-feature-name">Minimal documentation</strong>
                        <p className="reg-feature-detail">
                          Begin with the essential verification documents.
                        </p>
                      </div>
                    </div>

                    {/* Feature 3 */}
                    <div className="reg-feature-row">
                      <div className="reg-feature-icon-square">
                        <ShoppingBagIcon size={20} color="#a85050" />
                      </div>
                      <div className="reg-feature-text-block">
                        <strong className="reg-feature-name">Easy product management</strong>
                        <p className="reg-feature-detail">
                          Add and manage your products from one unified portal.
                        </p>
                      </div>
                    </div>

                    {/* Feature 4 */}
                    <div className="reg-feature-row">
                      <div className="reg-feature-icon-square">
                        <HeadphonesIcon size={20} color="#a85050" />
                      </div>
                      <div className="reg-feature-text-block">
                        <strong className="reg-feature-name">Seller support</strong>
                        <p className="reg-feature-detail">
                          Get dedicated support at every step of your journey.
                        </p>
                      </div>
                    </div>

                    {/* Feature 5: Safe & Secure (Bordered Box) */}
                    <div className="reg-safe-secure-box">
                      <div className="reg-safe-icon-box">
                        <ShieldCheckIcon size={24} color="#a85050" />
                      </div>
                      <div className="reg-safe-text-block">
                        <strong className="reg-safe-title">Safe &amp; Secure</strong>
                        <p className="reg-safe-detail">
                          Your data is encrypted and protected at every step.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── BOTTOM 4-COLUMN TRUST STRIP ── */}
              <div className="reg-trust-strip-card">
                <div className="reg-trust-col">
                  <div className="reg-trust-col-icon">
                    <ShieldCheckIcon size={22} color="#a85050" />
                  </div>
                  <div className="reg-trust-col-text">
                    <strong className="reg-trust-heading">Secure &amp; Trusted</strong>
                    <span className="reg-trust-subtext">Your data is always protected</span>
                  </div>
                </div>

                <div className="reg-trust-col">
                  <div className="reg-trust-col-icon">
                    <CheckBadgeIcon size={22} color="#a85050" />
                  </div>
                  <div className="reg-trust-col-text">
                    <strong className="reg-trust-heading">No Hidden Charges</strong>
                    <span className="reg-trust-subtext">Zero subscription or onboarding fees</span>
                  </div>
                </div>

                <div className="reg-trust-col">
                  <div className="reg-trust-col-icon">
                    <ClockTimerIcon size={22} color="#a85050" />
                  </div>
                  <div className="reg-trust-col-text">
                    <strong className="reg-trust-heading">Quick Verification</strong>
                    <span className="reg-trust-subtext">Get verified and start selling faster</span>
                  </div>
                </div>

                <div className="reg-trust-col">
                  <div className="reg-trust-col-icon">
                    <UsersGroupIcon size={22} color="#a85050" />
                  </div>
                  <div className="reg-trust-col-text">
                    <strong className="reg-trust-heading">Growing Community</strong>
                    <span className="reg-trust-subtext">Be part of 5,000+ successful sellers</span>
                  </div>
                </div>
              </div>
            </>
          )}

        </div>
      </main>

      <AuthFooter />
    </div>
  );
}

export default Register;
